// src/pages/items/ItemForm.tsx
import SelectDropdown from '@/components/common/SelectDropdown';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Textarea } from '@/components/ui/textarea';
import { currencies, taxPreferences } from '@/lib/constants/common';
import { itemTypes } from '@/lib/constants/items';
import { itemSchema } from '@/lib/schemas/item.schema';
import { itemsAPI } from '@/lib/utils/api';
import { ItemFormData } from '@/types/items.types';
import { Form, Formik, FormikHelpers } from 'formik';
import { ArrowLeft } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

const initialValues: ItemFormData = {
  itemType: 'service',
  itemName: '',
  sacCode: '',
  hsnCode: '',
  taxPreference: 'taxable',
  sellingPrice: '',
  currency: 'INR',
  description: '',
};

const ItemForm = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEditing = Boolean(id);

  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(isEditing);
  const [formData, setFormData] = useState<ItemFormData>(initialValues);

  // Fetch Item (Edit)
  useEffect(() => {
    if (!isEditing) return;

    const fetchItem = async () => {
      try {
        const res = await itemsAPI.getById(id as string);
        const item = res.data;

        setFormData({
          ...initialValues,
          ...item,
          sellingPrice: item.sellingPrice?.toString() || '',
          taxPreference: item.taxPreference || initialValues.taxPreference,
        });
      } catch {
        toast.error('Failed to fetch item');
        navigate('/items');
      } finally {
        setInitialLoading(false);
      }
    };

    fetchItem();
  }, [id, isEditing, navigate]);

  // Submit
  const handleSubmit = async (
    values: ItemFormData,
    { setSubmitting }: FormikHelpers<ItemFormData>
  ) => {
    setLoading(true);

    try {
      const payload = {
        ...values,
        sellingPrice: values.sellingPrice ? Number(values.sellingPrice) : 0,
        // Clear irrelevant code based on item type
        sacCode: values.itemType === 'service' ? values.sacCode : '',
        hsnCode: values.itemType === 'goods' ? values.hsnCode : '',
        taxPreference: values.taxPreference,
      };

      // isEditing ? await itemsAPI.update(id as string, payload) : await itemsAPI.create(payload);
      // toast.success(`Item ${isEditing ? 'updated' : 'created'} successfully`);
      // navigate('/items');
      let createdItem = null;

      if (isEditing) {
        await itemsAPI.update(id as string, payload);
      } else {
        const res = await itemsAPI.create(payload);
        createdItem = res.data.data; // or res.data.item if your API uses "item"
      }

      toast.success(`Item ${isEditing ? 'updated' : 'created'} successfully`);

      if (createdItem) {
        navigate('/items', { state: { newItem: createdItem } });
      } else {
        navigate('/items');
      }
    } catch (error: any) {
      toast.error(error?.response?.data?.message || 'Something went wrong');
    } finally {
      setLoading(false);
      setSubmitting(false);
    }
  };

  if (initialLoading) {
    return (
      <div className="flex h-dvh items-center justify-center">
        <div className="h-12 w-12 animate-spin rounded-full border-b-2 border-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6 pt-6">
      {/* Header */}
      <div className="flex items-center gap-2">
        <Button
          variant="ghost"
          onClick={() => navigate('/items')}
          className="text-gray-500 hover:text-gray-700"
        >
          <ArrowLeft className="h-3.5 w-3.5" /> Back
        </Button>
        <h1 className="text-2xl font-bold">{isEditing ? 'Edit Item' : 'Add Item'}</h1>
      </div>

      {/* Form */}
      <div className="bg-background rounded-lg shadow p-6">
        <Formik
          initialValues={formData}
          enableReinitialize
          validationSchema={itemSchema}
          onSubmit={handleSubmit}
        >
          {({ values, errors, touched, handleChange, setFieldValue }) => (
            <Form className="space-y-6">
              {/* ================= BASIC INFO ================= */}
              <div>
                <h3 className="text-lg font-medium mb-4">Basic Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Item Type */}
                  <div className="space-y-2">
                    <Label>Item Type *</Label>
                    <div className="space-y-1">
                      <SelectDropdown
                        value={values.itemType}
                        options={itemTypes}
                        onChange={(val) => {
                          setFieldValue('itemType', val);
                          // Clear the code fields when switching types
                          if (val === 'service') {
                            setFieldValue('hsnCode', '');
                          } else {
                            setFieldValue('sacCode', '');
                          }
                        }}
                      />
                      {touched.itemType && errors.itemType && (
                        <p className="text-sm text-red-500">{errors.itemType}</p>
                      )}
                    </div>
                  </div>

                  {/* Item Name */}
                  <div className="space-y-2">
                    <Label>Item Name *</Label>
                    <div className="space-y-1">
                      <Input
                        name="itemName"
                        value={values.itemName}
                        placeholder="Enter item name"
                        onChange={handleChange}
                      />
                      {touched.itemName && errors.itemName && (
                        <p className="text-sm text-red-500">{errors.itemName}</p>
                      )}
                    </div>
                  </div>

                  {/* SAC Code (for Services) */}
                  {values.itemType === 'service' && (
                    <div className="space-y-2">
                      <Label>SAC Code *</Label>
                      <div className="space-y-1">
                        <Input
                          name="sacCode"
                          value={values.sacCode}
                          placeholder="e.g., 998314"
                          onChange={handleChange}
                        />
                        {touched.sacCode && errors.sacCode && (
                          <p className="text-sm text-red-500">{errors.sacCode}</p>
                        )}
                      </div>
                    </div>
                  )}

                  {/* HSN Code (for Goods) */}
                  {values.itemType === 'goods' && (
                    <div className="space-y-2">
                      <Label>HSN Code *</Label>
                      <div className="space-y-1">
                        <Input
                          name="hsnCode"
                          value={values.hsnCode}
                          placeholder="e.g., 1234"
                          onChange={handleChange}
                        />
                        {touched.hsnCode && errors.hsnCode && (
                          <p className="text-sm text-red-500">{errors.hsnCode}</p>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Tax Preference */}
                  <div className="space-y-2">
                    <Label>Tax Preference *</Label>
                    <div className="space-y-1">
                      <SelectDropdown
                        value={values.taxPreference}
                        options={taxPreferences}
                        onChange={(val) => {
                          setFieldValue('taxPreference', val);
                        }}
                      />
                      {touched.taxPreference && errors.taxPreference && (
                        <p className="text-sm text-red-500">{errors.taxPreference}</p>
                      )}
                    </div>
                  </div>
                  {/* Currency */}
                  <div className="space-y-2">
                    <Label>Currency</Label>
                    <div className="space-y-1">
                      <SelectDropdown
                        value={values.currency}
                        options={currencies}
                        onChange={(val) => {
                          setFieldValue('currency', val);
                        }}
                      />
                      {touched.currency && errors.currency && (
                        <p className="text-sm text-red-500">{errors.currency}</p>
                      )}
                    </div>
                  </div>

                  {/* Selling Price */}
                  <div className="space-y-2">
                    <Label>Selling Price</Label>
                    <div className="space-y-1">
                      <Input
                        type="number"
                        name="sellingPrice"
                        min="0"
                        step="0.01"
                        value={values.sellingPrice}
                        placeholder="Enter selling price"
                        onChange={handleChange}
                      />
                      {touched.sellingPrice && errors.sellingPrice && (
                        <p className="text-sm text-red-500">{errors.sellingPrice}</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <Separator orientation="horizontal" className="!my-6" />

              {/* ================= DESCRIPTION ================= */}
              <div>
                <h3 className="text-lg font-medium mb-4">Additional Information</h3>
                <div className="space-y-2">
                  <Label>Description</Label>
                  <div className="space-y-1">
                    <Textarea
                      name="description"
                      value={values.description}
                      onChange={handleChange}
                      rows={4}
                      placeholder="Enter detailed description of the item..."
                    />
                    {touched.description && errors.description && (
                      <p className="text-sm text-red-500">{errors.description}</p>
                    )}
                  </div>
                </div>
              </div>

              {/* ================= ACTIONS ================= */}
              <div className="flex justify-end gap-4">
                <Button type="button" variant="outline" onClick={() => navigate('/items')}>
                  Cancel
                </Button>
                <Button type="submit" disabled={loading}>
                  {loading ? 'Saving...' : isEditing ? 'Update Item' : 'Create Item'}
                </Button>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </div>
  );
};

export default ItemForm;
